// zod-зеркало contracts/app-config.schema.json (draft 2020-12). Семантика — docs/tz/04-config-platform.md.
// Проверяем только форму (как JSON Schema): дубликаты slug, ссылки на шаблоны/продукты и прочую семантику
// проверяет валидатор черновика (мок/сервер), а не эта схема — см. journal SHY-61.
import { z } from "zod";

export const KNOWN_TARGETING_ATTRS = [
  "country",
  "locale",
  "app_version",
  "media_source",
  "campaign",
  "is_returning",
  "days_since_install",
  "entitlement",
] as const;
export type KnownTargetingAttr = (typeof KNOWN_TARGETING_ATTRS)[number];

export const CUSTOM_ATTR_PATTERN = /^custom\.[a-z0-9_]{1,32}$/;

export const slugSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_]{0,63}$/, "slug: ^[a-z0-9][a-z0-9_]{0,63}$");
export const semverSchema = z
  .string()
  .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/, "semver: X.Y.Z");
export const weightSchema = z.number().int().min(0).max(1_000_000);
// `uniqueItems: true` из contracts/app-config.schema.json: у zod отдельного правила нет,
// поэтому проверяем refine — без него дубликат capability на клиенте проходил, а на сервере нет.
export const capabilitiesSchema = z
  .array(slugSchema)
  .max(32)
  .refine((items) => new Set(items).size === items.length, "capabilities: значения не уникальны");
export const scalarSchema = z.union([z.string().max(64), z.number(), z.boolean()]);

const operatorKey = z.array(scalarSchema).min(1).max(64);
export const operatorSchema = z.union([
  z.object({ in: operatorKey }).strict(),
  z.object({ not_in: operatorKey }).strict(),
  z.object({ eq: scalarSchema }).strict(),
  z.object({ gte: scalarSchema }).strict(),
  z.object({ gt: scalarSchema }).strict(),
  z.object({ lte: scalarSchema }).strict(),
  z.object({ lt: scalarSchema }).strict(),
]);
export type Operator = z.infer<typeof operatorSchema>;
export type OperatorKind = keyof Operator;

// Условие типизируем вручную (а не через z.infer записи), чтобы "all"/"any"/"not" не смешивались с ключами
// условия в структурном союзе Rule — иначе TS не может сузить тип по `"all" in rule` (см. journal SHY-61).
export type ConditionKey = KnownTargetingAttr | `custom.${string}`;
export type Condition = Partial<Record<ConditionKey, Operator>>;

export const conditionSchema: z.ZodType<Condition> = z.record(z.string(), operatorSchema).refine(
  (obj) => {
    const keys = Object.keys(obj);
    if (keys.length !== 1) return false;
    const key = keys[0]!;
    return (
      (KNOWN_TARGETING_ATTRS as readonly string[]).includes(key) || CUSTOM_ATTR_PATTERN.test(key)
    );
  },
  { message: "condition: ровно один атрибут (известный или custom.<key>)" },
);

export interface RuleAll {
  all: Rule[];
}
export interface RuleAny {
  any: Rule[];
}
export interface RuleNot {
  not: Rule;
}
export type Rule = RuleAll | RuleAny | RuleNot | Condition;

export const ruleSchema: z.ZodType<Rule> = z.lazy(() =>
  z.union([
    z.object({ all: z.array(ruleSchema).min(1).max(16) }).strict(),
    z.object({ any: z.array(ruleSchema).min(1).max(16) }).strict(),
    z.object({ not: ruleSchema }).strict(),
    conditionSchema,
  ]),
);

export const targetingSchema = z.union([z.null(), ruleSchema]);
export type Targeting = z.infer<typeof targetingSchema>;

const variantBaseShape = {
  slug: slugSchema,
  weight: weightSchema,
  enabled: z.boolean(),
  min_app_version: semverSchema,
  capabilities: capabilitiesSchema,
  targeting: targetingSchema.optional(),
  fallback: z.boolean().optional(),
};

export const onboardingStepSchema = z
  .object({
    template: slugSchema,
    params: z.record(z.string(), z.unknown()),
  })
  .strict();
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

export const paywallInsertionSchema = z
  .object({
    after_step: z.number().int().min(0).max(11),
    placement: slugSchema,
  })
  .strict();
export type PaywallInsertion = z.infer<typeof paywallInsertionSchema>;

export const onboardingVariantSchema = z
  .object({
    ...variantBaseShape,
    params: z
      .object({
        steps: z.array(onboardingStepSchema).min(1).max(12),
        att_after_step: z.number().int().min(0).max(11).optional(),
        paywall_insertions: z.array(paywallInsertionSchema).max(4).optional(),
      })
      .strict(),
  })
  .strict();
export type OnboardingVariant = z.infer<typeof onboardingVariantSchema>;

export const paywallVariantSchema = z
  .object({
    ...variantBaseShape,
    template: slugSchema,
    params: z.record(z.string(), z.unknown()),
  })
  .strict();
export type PaywallVariant = z.infer<typeof paywallVariantSchema>;

export const paywallGroupSchema = z
  .object({
    parent: z.union([z.null(), slugSchema]),
    variants: z.array(paywallVariantSchema).max(32),
  })
  .strict();
export type PaywallGroup = z.infer<typeof paywallGroupSchema>;

// `required: ["value"]` в контракте. В zod 4 `z.unknown()` внутри объекта НЕ делает ключ
// необязательным (отсутствие даёт `invalid_type: nonoptional`) — проверено, отдельный refine не
// нужен. Тест в appConfig.test.ts закрепляет это: приписанный когда-нибудь `.optional()` его уронит.
export const flagValueSchema = z
  .object({
    value: z.unknown(),
    targeting: targetingSchema.optional(),
  })
  .strict();
export type FlagValue = z.infer<typeof flagValueSchema>;

export const lookPackSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
    version: z.number().int().min(1),
    min_engine_version: semverSchema,
    checksum: z.string().regex(/^sha256:[0-9a-f]{64}$/),
    // Контракт требует `format: "uri"` — это НЕ то же, что `z.string().url()`: последний
    // отвергает относительный URI, и относительная ссылка на пак роняла разбор всего документа,
    // то есть весь экран конфигурации (P3-11). Проверяем ровно то, что проверяет контракт:
    // непустую строку до 512 символов без пробелов.
    url: z
      .string()
      .min(1)
      .max(512)
      .refine((v) => !/\s/.test(v), "url: пробелы недопустимы"),
    signature: z.string().regex(/^ed25519:[A-Za-z0-9+/=_-]{60,128}$/),
    size_bytes: z.number().int().min(1),
  })
  .strict();
export type LookPack = z.infer<typeof lookPackSchema>;

export const onboardingSurfaceSchema = z
  .object({
    key: z.literal("onboarding"),
    kind: z.literal("onboarding"),
    salt: z.string().min(1).max(64),
    variants: z.array(onboardingVariantSchema).max(32),
  })
  .strict();

export const paywallSurfaceSchema = z
  .object({
    key: z.string().regex(/^paywall_[a-z0-9_]{1,48}$/),
    kind: z.literal("paywall"),
    salt: z.string().min(1).max(64),
    groups: z.array(paywallGroupSchema).min(1).max(33),
  })
  .strict();

export const flagsSurfaceSchema = z
  .object({
    key: z.literal("flags"),
    kind: z.literal("flags"),
    values: z
      .record(z.string().regex(/^[a-z0-9][a-z0-9_]{0,63}$/), flagValueSchema)
      .refine((obj) => Object.keys(obj).length <= 64, "flags: не более 64 ключей"),
  })
  .strict();

export const lookPacksSurfaceSchema = z
  .object({
    key: z.literal("look_packs"),
    kind: z.literal("look_packs"),
    packs: z.array(lookPackSchema).max(64),
  })
  .strict();

export const surfaceSchema = z.discriminatedUnion("kind", [
  onboardingSurfaceSchema,
  paywallSurfaceSchema,
  flagsSurfaceSchema,
  lookPacksSurfaceSchema,
]);
export type Surface = z.infer<typeof surfaceSchema>;
export type OnboardingSurface = z.infer<typeof onboardingSurfaceSchema>;
export type PaywallSurface = z.infer<typeof paywallSurfaceSchema>;
export type FlagsSurface = z.infer<typeof flagsSurfaceSchema>;
export type LookPacksSurface = z.infer<typeof lookPacksSurfaceSchema>;

export const lookPolicySchema = z
  .object({
    enabled: z.boolean(),
    premium: z.boolean(),
    strength: z.number().int().min(0).max(100),
    order: z.number().int().min(0).max(9999),
  })
  .strict();
export type LookPolicy = z.infer<typeof lookPolicySchema>;

export const appConfigSchema = z
  .object({
    schema_version: z.literal(1),
    look_catalog: z
      .partialRecord(
        z.enum([
          "P001",
          "P002",
          "P003",
          "P004",
          "P009",
          "P011",
          "P017",
          "P019",
          "P025",
          "P027",
          "P033",
          "P035",
          "P041",
          "P042",
          "P049",
          "P057",
          "P058",
          "P061",
          "P065",
          "P068",
          "P076",
          "P081",
          "P082",
          "P089",
        ]),
        lookPolicySchema,
      )
      .optional(),
    revision: z
      .object({
        number: z.number().int().min(0),
        published_at: z.string().nullable().optional(),
        note: z.string().max(500).nullable().optional(),
      })
      .strict(),
    surfaces: z.array(surfaceSchema).max(32),
  })
  .strict();
export type AppConfig = z.infer<typeof appConfigSchema>;
